// @flow
import React from 'react'
import { reduxForm, formValueSelector } from 'redux-form'
import { Field } from 'redux-form'
import { connect } from 'react-redux'
import type { Form } from 'lib/types'
import { Button } from 'components/Button'
import { SUBACCOUNT_FOR_ACCOUNT_CREATION, INVESTMENT_TYPES, INVESTMENT_COMPANIES } from 'lib/constants'
import states from 'lib/states'
import { renderSelectField, renderInputField } from 'components/FormFields'
import { unsetModal } from 'lib/modal/actions'
import { setBanner, setLoad, setUnload } from 'lib/notification/actions'
import { createSubaccount } from 'lib/subaccount/actions'
import styles from './account.css'

type Props = {
  accountNumber: string,
  dispatch: Function,
  form: Form,
  onSuccess?: Function,
  subaccountType?: string,
  vin?: string,
}

const filterAbbreviation = (abbreviation, subaccountType) => {
  if (!abbreviation || !subaccountType) return abbreviation

  let filtered = abbreviation

  const keywordsToExclude = {
    home: ['home'],
    land: ['land'],
    mortgage: ['mortgage'],
  }

  const excludeWords = keywordsToExclude[subaccountType] || []

  excludeWords.forEach((word) => {
    const regex = new RegExp(`\\b${word}-?\\b`, 'gi')
    filtered = filtered.replace(regex, '').trim()
  })

  filtered = filtered.replace(/\s+/g, ' ').trim()

  return filtered
}

const onSubmit = (data, dispatch, props) => {
  const { accountNumber, onSuccess } = props

  dispatch(setLoad())

  const postData = {
    subaccount_type: data.subaccountType,
  }

  if (['home', 'land', 'mortgage'].includes(data.subaccountType)) {
    if (data.addressLine1) postData.street_1 = data.addressLine1
    if (data.addressLine2) postData.street_2 = data.addressLine2
    if (data.city) postData.city = data.city
    if (data.state) postData.state = data.state
    if (data.zip) postData.zip = data.zip
    if (data.abbreviation) {
      const filteredAbbreviation = filterAbbreviation(data.abbreviation, data.subaccountType)
      postData.location_desc = filteredAbbreviation
    }
  } else if (data.subaccountType === 'vehicle') {
    postData.vin = data.vin
  } else if (data.subaccountType === 'individual_investment') {
    if (data.investmentType) postData.investment_type = data.investmentType
    if (data.investmentCompany) postData.investor = data.investmentCompany
    if (data.accountLastFour) postData.account_suffix = data.accountLastFour
  }

  return dispatch(createSubaccount(accountNumber, postData))
    .then(() => {
      dispatch(setBanner('Subaccount successfully created'))
      if (onSuccess && typeof onSuccess === 'function') {
        onSuccess()
      }
      window.location.reload()
    })
    .catch((error) => {
      let errorMessage = 'Subaccount creation failed'

      if (error && error.error && error.error.message) {
        errorMessage = error.error.message
      } else if (error && error.message) {
        errorMessage = error.message
      } else if (typeof error === 'string') {
        errorMessage = error
      }

      dispatch(setBanner(errorMessage, 'danger'))
    })
    .finally(() => {
      dispatch(unsetModal())
      dispatch(setUnload())
    })
}

const Component = ({ accountNumber, form, onSuccess, subaccountType, vin }: Props) => {
  const handleSubmit = form.handleSubmit((data, dispatch) => {
    return onSubmit(data, dispatch, { accountNumber, onSuccess })
  })

  const handleCancel = () => {
    return form.dispatch(unsetModal())
  }

  const showAddressFields = ['home', 'land', 'mortgage'].includes(subaccountType)
  const showInvestmentFields = subaccountType === 'individual_investment'
  const showVehicleFields = subaccountType === 'vehicle'
  const subaccountTypeOptions = SUBACCOUNT_FOR_ACCOUNT_CREATION

  return (
    <section>
      <h4 className={styles.section}>Add Subaccount</h4>
      <form onSubmit={handleSubmit}>
        <div className={styles.section}>
          <div className={styles.formItemHalf}>
            <Field
              component={renderSelectField}
              options={subaccountTypeOptions}
              name="subaccountType"
              label="Subaccount Type"
              placeholderText="Please choose Subaccount type"
            />
          </div>

          {showAddressFields && (
            <>
              <div className={styles.formItemHalf}>
                <Field component={renderInputField} label="Address" name="addressLine1" placeholder="Street 1" required={true} />
              </div>

              <div className={styles.formItemHalf}>
                <Field component={renderInputField} name="addressLine2" placeholder="Street 2" />
              </div>

              <div className={styles.stateZipContainer}>
                <div className={styles.cityField}>
                  <Field component={renderInputField} name="city" placeholder="City" required={true} />
                </div>
                <div className={styles.stateField}>
                  <Field component={renderSelectField} options={states} name="state" placeholder="State" placeholderText="State" required={true} />
                </div>
              </div>
              <div className={styles.zipField}>
                <Field component={renderInputField} name="zip" placeholder="Zip (5 digit)" required={true} />
              </div>

              <div className={styles.formItemHalf}>
                <Field
                  component={renderInputField}
                  name="abbreviation"
                  label="Abbreviation for Account Name"
                  placeholder="ex: 123 Main"
                  required={true}
                />
              </div>
              <div className={styles.noteContainer}>
                <p className={styles.noteText}>
                  <strong>Please note:</strong>
                  After account creation you may add and edit additional details including insurance, taxes, etc.
                </p>
              </div>
            </>
          )}

          {showVehicleFields && (
            <>
              <div className={styles.formItemHalf}>
                <Field component={renderInputField} name="vin" label="VIN" placeholder="ex: 5TDKDRBH1PS123456" required={true} />
              </div>
            </>
          )}

          {showInvestmentFields && (
            <>
              <div className={styles.formItemHalf}>
                <Field
                  component={renderSelectField}
                  options={INVESTMENT_TYPES}
                  name="investmentType"
                  label="Type of Individual Investment"
                  placeholder="Please choose individual investment type"
                  required={true}
                />
              </div>

              <div className={styles.formItemHalf}>
                <Field
                  component={renderSelectField}
                  options={INVESTMENT_COMPANIES}
                  name="investmentCompany"
                  label="Investment Company"
                  placeholder="Please choose company"
                  required={true}
                />
              </div>

              <div className={styles.formItemQuarter}>
                <Field
                  component={renderInputField}
                  name="accountLastFour"
                  label="Last 4 digits of Investment Account"
                  placeholder="ex: 2312"
                  required={true}
                />
              </div>
            </>
          )}
        </div>

        <div className={styles.row}>
          <Button size="default" color="red" label="Cancel" onClick={handleCancel} type="button" />
          <Button size="default" color="blue" label="Create Subaccount" type="submit" disabled={!subaccountType || (subaccountType === 'vehicle' && !(vin && vin.trim()))} />
        </div>
      </form>
    </section>
  )
}

const AddSubaccountForm = reduxForm({
  form: 'addSubaccount',
  propNamespace: 'form',
})(Component)

const selector = formValueSelector('addSubaccount')

const AddSubaccountModal = connect((state) => ({
  subaccountType: selector(state, 'subaccountType'),
  vin: selector(state, 'vin'),
}))(AddSubaccountForm)

export default AddSubaccountModal
